/**
 * constants for map (paddings/margins, shared variables)
 *
 * for configurations that will change ESRI map behavior/functionalities, put them in 'configurations'
 */
import { keyBy } from 'lodash';
import { EsriMapEntityTypeEnum } from './types';

export const LOCATION_OUT_OF_DATE_TIME = 300000;
export const PROPERTIES_PANEL_WIDTH = 288;
export const FR_PROPERTIES_PANEL_WIDTH = 310;
export const PROPERTIES_PANEL_FOOTER_HEIGHT = 64;
export const PROPERTIES_PANEL_MARGIN_TOP = 57;
export const FR_PROPERTIES_PANEL_MARGIN_TOP = 73;
export const MAP_TOP_MENU_BAR_MARGIN_LEFT = 0;
export const MAP_TOP_MENU_BAR_MARGIN_TOP = 0;

export const AML_UNCERTAINTY_CIRCLE_COLOR = [84, 163, 255, 0.5];

export const EVENT_RIGHT_CLICK_BUTTON_NUMBER = 2;

export const ESRI_MAP_FULL_LOAD_DELAY_INITIAL_MS = 2500;
export const ESRI_MAP_FULL_LOAD_DELAY_REOPEN_MS = 1000;

export const POLYLINES = {
    EVENT_TO_UNIT: 'EVENT_TO_UNIT',
    UNIT_TO_USER: 'UNIT_TO_USER',
    EVENT_TO_E911: 'EVENT_TO_E911',
    EVENT_TO_AML: 'EVENT_TO_AML',
    GEO_POINT_TO_E911: 'GEO_POINT_TO_E911',
    GEO_POINT_TO_AML: 'GEO_POINT_TO_AML',
    E911_TO_AML: 'E911_TO_AML',
} as const;

export const FEATURE_LAYERS_PROPERTIES = {
    EVENTS: {
        layerKey: '_events',
        title: 'Events',
        layerId: 1000,
    },
    UNITS: {
        layerKey: '_units',
        title: 'Units',
        layerId: 2000,
        clusterPopupClassName: 'unitClusterPopup',
    },
    STATIONS: {
        layerKey: '_stations',
        title: 'Stations',
        layerId: 3000,
    },
    HOSPITALS: {
        layerKey: '_hospitals',
        title: 'Hospitals',
        layerId: 4000,
    },
    NEARBY_GEO_CAUTIONS: {
        layerKey: '_nearby_geo_cautions',
        title: 'Nearby Geo Cautions',
        layerId: 5000,
    },
    INITIAL_GEO_POINT_PIN: {
        layerKey: '_initial_geo_point_pin',
        title: 'Initial Geo Point Pin',
        layerId: 6000,
    },
    GEO_POINT_LOCATIONS: {
        layerKey: '_geo_point_locations',
        title: 'Geo Point Locations',
        layerId: 7000,
    },
    PHONE_CALLS: {
        layerKey: '_phone_calls',
        title: 'Phone Calls',
        layerId: 8000,
    },
    AML: {
        layerKey: '_aml',
        title: 'AML',
        layerId: 9000,
    },
    AML_UNCERTAINTY_RADIUS: {
        layerKey: '_aml_uncertainty_radius',
        title: 'AML Uncertainty Radius',
        layerId: 10000,
    },
    AML_LOCATION_HISTORY: {
        layerKey: '_aml_location_history',
        title: 'AML Location History',
        layerId: 11000,
    },
    POLYLINES: {
        layerKey: '_polylines',
        title: 'Polylines',
        layerId: 12000,
    },
    SENSORS: {
        layerKey: '_sensors',
        title: 'Sensors',
        layerId: 13000,
    },
} as const;

export const ENTITY_TYPE_TO_FEATURE_LAYER_KEY_MAP = {
    [EsriMapEntityTypeEnum.UNIT]: FEATURE_LAYERS_PROPERTIES.UNITS.layerKey,
    [EsriMapEntityTypeEnum.USER]: FEATURE_LAYERS_PROPERTIES.UNITS.layerKey,
    [EsriMapEntityTypeEnum.EVENT]: FEATURE_LAYERS_PROPERTIES.EVENTS.layerKey,
    [EsriMapEntityTypeEnum.STATION]: FEATURE_LAYERS_PROPERTIES.STATIONS.layerKey,
    [EsriMapEntityTypeEnum.HOSPITAL]: FEATURE_LAYERS_PROPERTIES.HOSPITALS.layerKey,
    [EsriMapEntityTypeEnum.GEO_POINT_LOCATION]:
        FEATURE_LAYERS_PROPERTIES.GEO_POINT_LOCATIONS.layerKey,
    [EsriMapEntityTypeEnum.PHONE_CALL]: FEATURE_LAYERS_PROPERTIES.PHONE_CALLS.layerKey,
    [EsriMapEntityTypeEnum.AML_CALL]: FEATURE_LAYERS_PROPERTIES.AML.layerKey,
    [EsriMapEntityTypeEnum.SENSOR]: FEATURE_LAYERS_PROPERTIES.SENSORS.layerKey,
};

export const SHORTCUTS = [
    {
        text: 'Zoom out',
        command: '-',
        code: 'Minus',
    },
    {
        text: 'Zoom in',
        command: '+',
        code: 'Equal',
    },
    {
        text: 'Reset zoom level',
        command: '0',
        code: 'Digit0',
    },
    {
        text: 'Focus selected unit',
        command: '1',
        code: 'Digit1',
    },
    {
        text: 'Focus selected event',
        command: '2',
        code: 'Digit2',
    },
    {
        text: 'Reset position and zoom level',
        command: '3',
        code: 'Digit3',
    },
    {
        text: 'Bring unit layer to top',
        command: 'u',
        code: 'KeyU',
    },
    {
        text: 'Bring event layer to top',
        command: 'e',
        code: 'KeyE',
    },
] as const;

export const SHORTCUTS_BY_CODE = keyBy(SHORTCUTS, 'code');
